import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { trackPurchase } from "@/lib/analytics";
import { OrderStatusTimeline } from "@/components/shop/OrderStatusTimeline";
import { CheckCircle2, Truck, Store, MapPin, XCircle } from "lucide-react";
import { STORE_ADDRESS, GOOGLE_REVIEW_URL } from "@/constants/store";

export default function OrderSuccessPage() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const email = params.get("email");
  const [order, setOrder] = useState(null);

  const fetchOrder = useCallback(() => {
    const url = `/orders/lookup/${orderNumber}` + (email ? `?email=${encodeURIComponent(email)}` : "");
    api.get(url).then((r) => { setOrder(r.data); trackPurchase(r.data); }).catch(() => {});
  }, [orderNumber, email]);

  const terminal = order && ["completed", "cancelled"].includes(order.status);

  useEffect(() => {
    fetchOrder();
    if (terminal) return;
    const interval = setInterval(fetchOrder, 15000);
    return () => clearInterval(interval);
  }, [fetchOrder, terminal]);

  if (!order) {
    return <div className="p-20 text-center ipv-eyebrow">Loading order…</div>;
  }

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-12 py-16" data-testid="order-success-page">
      <div className="text-center">
        {order.status === "cancelled" ? (
          <>
            <XCircle className="w-14 h-14 mx-auto text-[#DC2626]" />
            <div className="ipv-eyebrow text-[#DC2626] mt-6">Order cancelled</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">
              Order {order.order_number}
            </h1>
            <p className="text-gray-600 mt-4 max-w-md mx-auto">
              This order has been cancelled. Questions? Call us at{" "}
              <a href="tel:+16164100158" className="font-bold text-black">+1 (616) 410-0158</a>.
            </p>
          </>
        ) : (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-green-600" />
            <div className="ipv-eyebrow text-[#0A1C40] mt-6">Order confirmed</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">
              Thanks, {order.customer_name.split(" ")[0]}!
            </h1>
            <p className="text-gray-600 mt-4 max-w-md mx-auto">
              {order.is_hold ? (
                <>Your <b>$50 deposit</b> is received and order{" "}
                <span className="font-bold text-black" data-testid="order-number">{order.order_number}</span>{" "}
                is <b>ON HOLD for 7 days</b>. Pay the balance in store or by the payment link we'll send you.</>
              ) : (
                <>Your order <span className="font-bold text-black" data-testid="order-number">{order.order_number}</span> is paid and confirmed.
                We've emailed your receipt.</>
              )}
            </p>
          </>
        )}
      </div>

      <div className="mt-12">
        <OrderStatusTimeline order={order} />
      </div>

      <div className="mt-8 border border-gray-200">
        <div className="p-6 border-b border-gray-200 grid sm:grid-cols-2 gap-6">
          <div>
            <div className="ipv-eyebrow mb-2">Order</div>
            <div className="text-sm space-y-1">
              <div><b>{order.order_number}</b></div>
              <div className="text-gray-600">{new Date(order.created_at).toLocaleString()}</div>
              <div>Status: <span className="font-bold uppercase text-[#0A1C40]">{order.status}</span></div>
            </div>
          </div>
          <div>
            <div className="ipv-eyebrow mb-2 flex items-center gap-1">
              {order.fulfillment === "pickup" ? <Store className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
              {order.fulfillment === "pickup" ? "Pickup" : "Delivery"}
            </div>
            {order.fulfillment === "pickup" ? (
              <div className="text-sm">
                <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {STORE_ADDRESS.short}</div>
                <div className="text-gray-600 mt-1">We'll text you when ready (~1 hour)</div>
                {order.pickup_date && (
                  <div className="mt-1 font-bold text-[#0A1C40]" data-testid="order-pickup-date">
                    🗓 Preferred pickup: {order.pickup_date}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm">
                <div>{order.delivery_address}</div>
                <div>{order.delivery_city}, {order.delivery_state} {order.delivery_zip}</div>
                {order.delivery_date && (
                  <div className="mt-1 font-bold text-[#0A1C40]" data-testid="order-delivery-date">
                    🚚 Scheduled: {order.delivery_date}
                    {order.delivery_window ? ` · ${order.delivery_window}` : ""}
                  </div>
                )}
                {order.rush && !order.delivery_date && (
                  <div className="mt-1 font-bold text-[#8a6a00]">⚡ Rush — we'll call to confirm timing</div>
                )}
                {order.delivery_info && (
                  <div className="text-gray-600 mt-1">{order.delivery_info.miles} mi from showroom</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {order.line_items.map((li) => (
            <div key={li.product_id} className="p-5 flex justify-between text-sm">
              <div>
                <div className="font-bold">{li.quantity} × {li.name}{li.color ? ` — ${li.color}` : ""}</div>
                {li.sku && <div className="text-gray-500 text-xs mt-0.5">SKU {li.sku}</div>}
                {li.deal_applied && (
                  <div className="text-green-700 text-xs mt-0.5 font-bold">Deal: saved {fmtMoney(li.deal_applied.savings)}</div>
                )}
                {li.pallet_contents?.length > 0 && (
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5" data-testid="order-pallet-picks">
                    <div className="font-bold text-[#0A1C40]">Your picks:</div>
                    {li.pallet_contents.map((c, i) => (
                      <div key={i}>• {c.count}× {c.name}{c.sku ? ` [${c.sku}]` : ""}</div>
                    ))}
                  </div>
                )}
                {li.swap_notes?.length > 0 && (
                  <div className="text-xs text-amber-700 mt-1" data-testid="order-swap-note">Swap request: {li.swap_notes.join(" | ")}</div>
                )}
              </div>
              <div className="font-bold">{fmtMoney(li.line_total)}</div>
            </div>
          ))}
        </div>

        <div className="p-6 bg-[#F4F4F5] space-y-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{fmtMoney(order.subtotal)}</span></div>
          {order.discount && <div className="flex justify-between text-[#DC2626]"><span>Discount ({order.discount.code})</span><span>-{fmtMoney(order.discount_amount)}</span></div>}
          <div className="flex justify-between"><span>Tax</span><span>{fmtMoney(order.tax)}</span></div>
          <div className="flex justify-between"><span>Delivery</span><span>{order.fulfillment === "pickup" ? "Free pickup" : fmtMoney(order.delivery_fee)}</span></div>
          {order.is_hold && order.status === "on_hold" ? (
            <div className="pt-3 border-t border-gray-300 space-y-1" data-testid="hold-payment-summary">
              <div className="flex justify-between font-display font-black text-xl"><span>Order total</span><span>{fmtMoney(order.total)}</span></div>
              <div className="flex justify-between text-green-700 font-bold"><span>Deposit paid</span><span>{fmtMoney(order.deposit_amount || 50)}</span></div>
              <div className="flex justify-between font-bold text-[#0A1C40]"><span>Balance due</span><span>{fmtMoney(order.balance_due || 0)}</span></div>
              {order.hold_expires_at && (
                <div className="text-xs text-gray-500 pt-1">Held until {new Date(order.hold_expires_at).toLocaleDateString()} — deposit applies to your total.</div>
              )}
            </div>
          ) : (
            <div className="flex justify-between pt-3 border-t border-gray-300 font-display font-black text-xl">
              <span>Total Paid</span><span>{fmtMoney(order.total)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 bg-[#FFC220] rounded-lg p-6 text-center" data-testid="order-google-review-banner">
        <div className="font-display font-black text-[#0A1C40] text-lg">Happy with your deal? ⭐</div>
        <p className="text-sm text-[#0A1C40]/85 mt-1">
          A quick Google review helps our small Grand Rapids business more than you know.
        </p>
        <a
          href={GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 mt-3 bg-[#0A1C40] text-[#FFC220] font-bold px-6 py-2.5 rounded-full text-sm hover:bg-black transition-colors"
          data-testid="order-google-review-btn"
        >
          ★ Review us on Google
        </a>
      </div>

      <div className="mt-10 flex justify-center gap-3">
        <Link to="/shop" className="btn-primary">Keep shopping</Link>
        <Link to="/track" className="btn-outline">Track order</Link>
      </div>
    </div>
  );
}
